import { MouseEvent } from 'react';

import pluralize from 'pluralize';

import {
  Icons,
  ListItemText,
  Tooltip,
  withProps,
  Box,
  tokens,
} from '@cast/design-system';
import { useUser } from '@cast/infrastructure-auth';
import { OrganizationAvatar } from '@cast/shared-organization-ui';
import { useRbacEnterpriseRoleBindings } from '@cast/shared-rbac-data-access';
import { Organization } from '@cast/types';

import {
  useNavigate,
  useSetViewedOrganizationId,
} from '@cast/infrastructure-organization';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const OrganizationListItemText = withProps(ListItemText, {
  primaryTypographyProps: { variant: 'P12M', component: 'span' },
  secondaryTypographyProps: { variant: 'P10R' },
});

type EnterpriseHeaderProps = {
  selectedEnterprise: Organization;
  selectedEnterpriseChildrenCount: number;
  childOrganizations: Organization[];
  activeOrganizationId?: string;
  onBack: () => void;
};

export const EnterpriseHeader = ({
  selectedEnterprise,
  selectedEnterpriseChildrenCount,
  childOrganizations,
  activeOrganizationId,
  onBack,
}: EnterpriseHeaderProps) => {
  const navigate = useNavigate();
  const setViewedOrganizationId = useSetViewedOrganizationId();
  const { setCurrentOrganization } = useOrganizations();

  const user = useUser();
  const { roleBindings, isLoadingRoleBindings } = useRbacEnterpriseRoleBindings(
    {
      enterpriseId: selectedEnterprise.id,
      subjectId: [user.guid],
    }
  );
  const canViewEnterprise =
    !isLoadingRoleBindings && (roleBindings ?? []).length > 0;

  const handleEnterpriseClick = (event?: MouseEvent) => {
    // Determine which organization to select
    let targetOrganization: Organization;

    if (childOrganizations.length === 0) {
      // No children: use enterprise itself
      // This matches existing behavior in determineCurrentOrganization
      targetOrganization = selectedEnterprise;
    } else {
      // Prefer current active org if it's a child of this enterprise (better UX)
      const activeChild = childOrganizations.find(
        (org) => org.id === activeOrganizationId
      );
      targetOrganization = activeChild || childOrganizations[0];
    }

    setCurrentOrganization(targetOrganization.id, { skipRedirect: true });
    setViewedOrganizationId(selectedEnterprise.id);
    // Navigate to enterprise overview with the child org ID
    navigate(
      `/organization/enterprise/${selectedEnterprise.id}/overview?org=${targetOrganization.id}`,
      {
        doNotForwardOrganizationId: true,
        clickEvent: event,
      }
    );
  };

  const secondaryText = `Enterprise | ${selectedEnterpriseChildrenCount} ${pluralize(
    'child organization',
    selectedEnterpriseChildrenCount
  )}`;

  if (!selectedEnterprise?.id || !selectedEnterprise?.name) {
    return null;
  }

  return (
    <Box
      sx={{
        borderBottom: tokens.theme.border.structural.neutral,
        display: 'flex',
      }}
      role="banner"
      aria-label="Enterprise header"
    >
      {/* Back Button */}
      <Box
        data-testid="org-menu-list-back-btn"
        onClick={onBack}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          minHeight: '56px',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: tokens.theme.bg.action.surfaceHover,
          },
        }}
        role="button"
        aria-label="Back to main organization list"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onBack();
          }
        }}
      >
        <Box
          sx={{
            height: '16px',
            color: tokens.theme.fg.structural.secondary,
          }}
        >
          <Icons.CaretLeft size="16px" />
        </Box>
      </Box>
      {/* Organization Info */}
      <Tooltip
        wrapIf={!canViewEnterprise}
        title="You don't have permissions to access this enterprise"
        placement="bottom-start"
      >
        <Box
          data-testid="active-enterprise"
          onClick={canViewEnterprise ? handleEnterpriseClick : undefined}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '8px 16px 8px 8px',
            cursor: canViewEnterprise ? 'pointer' : 'default',
            opacity: canViewEnterprise ? 1 : 0.4,
            '&:hover': canViewEnterprise
              ? {
                  backgroundColor: tokens.theme.bg.action.surfaceHover,
                }
              : undefined,
          }}
          role="button"
          aria-label={
            canViewEnterprise
              ? `Navigate to ${selectedEnterprise.name} enterprise / organization overview`
              : `${selectedEnterprise.name} enterprise (no access)`
          }
          aria-disabled={!canViewEnterprise}
          tabIndex={canViewEnterprise ? 0 : -1}
          onKeyDown={(e) => {
            if (canViewEnterprise && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleEnterpriseClick();
            }
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <OrganizationAvatar
              size="md"
              organizationId={selectedEnterprise.id}
            />
            {selectedEnterprise.organizationMember === false && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  transform: 'translate(50%, 50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: tokens.theme.bg.structural.surface,
                  border: '1px solid',
                  borderColor: tokens.theme.border.structural.neutral,
                  borderRadius: '50%',
                  padding: '1px',
                  color: tokens.theme.fg.structural.primary,
                }}
              >
                <Icons.ShieldWarning size="12px" />
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <OrganizationListItemText
              primary={selectedEnterprise.name}
              secondary={secondaryText}
              sx={{ userSelect: 'text' }}
            />
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
};
